<script setup>
import { computed, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';

const props = defineProps({
  conversationTeamId: {
    type: [Number, null],
    default: null,
  },
  selectedTeamId: {
    type: [Number, null],
    default: null,
  },
  selectedInboxId: {
    type: [Number, null],
    default: null,
  },
});

const emit = defineEmits(['teamSelected']);
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const teams = useMapGetter('teams/getTeams');
const { isAdmin } = useAdmin();

// Get the agent's authorized teams that have email inboxes
const authorizedEmailTeams = computed(() => {
  if (!teams.value || !teams.value.length) return [];

  return teams.value.filter(team => {
    // Admin sees all teams; agents see only their teams
    const isMember = team.is_member !== undefined ? team.is_member : team.isMember;
    const hasAccess = isAdmin.value || isMember;
    
    // Team must have at least one email inbox configured
    const emailInboxes = team.email_inboxes || team.emailInboxes;
    const hasEmailInbox = emailInboxes && emailInboxes.length > 0;
    
    return hasAccess && hasEmailInbox;
  });
});

// Get email inboxes for the currently selected team
const selectedTeamInboxes = computed(() => {
  if (!props.selectedTeamId) return [];
  const team = authorizedEmailTeams.value.find(
    t => t.id === props.selectedTeamId
  );
  return team?.email_inboxes || team?.emailInboxes || [];
});

// Resolved "From" email for the current selection
const resolvedFromEmail = computed(() => {
  if (!props.selectedInboxId || !selectedTeamInboxes.value.length) return '';
  const inbox = selectedTeamInboxes.value.find(
    i => i.id === props.selectedInboxId
  );
  return inbox?.email || '';
});

const showInboxSelector = computed(() => {
  return selectedTeamInboxes.value.length > 1;
});

function onTeamChange(event) {
  const teamId = Number(event.target.value);
  if (!teamId) {
    emit('teamSelected', { teamId: null, inboxId: null });
    return;
  }
  const team = authorizedEmailTeams.value.find(t => t.id === teamId);
  const emailInboxes = team?.email_inboxes || team?.emailInboxes;
  const firstInbox = emailInboxes?.[0];
  emit('teamSelected', {
    teamId,
    inboxId: firstInbox?.id || null,
  });
}

function onInboxChange(event) {
  const inboxId = Number(event.target.value);
  emit('teamSelected', {
    teamId: props.selectedTeamId,
    inboxId,
  });
}

// Initialize default team on mount
watch(
  authorizedEmailTeams,
  newTeams => {
    if (!newTeams.length) return;
    // Don't override if already selected
    if (props.selectedTeamId) return;

    // Priority: conversation team > first authorized team
    const conversationTeam = props.conversationTeamId
      ? newTeams.find(t => t.id === props.conversationTeamId)
      : null;

    const defaultTeam = conversationTeam || newTeams[0];
    if (defaultTeam) {
      const emailInboxes = defaultTeam.email_inboxes || defaultTeam.emailInboxes;
      const firstInbox = emailInboxes?.[0];
      emit('teamSelected', {
        teamId: defaultTeam.id,
        inboxId: firstInbox?.id || null,
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="w-full">
    <div v-if="authorizedEmailTeams.length === 0" class="text-sm text-n-slate-11 italic flex items-center h-7 px-3">
      No teams with email inboxes found
    </div>
    <div v-else class="input-group small !mb-0">
      <div class="flex-1 min-w-0 m-0 rounded-none whitespace-nowrap">
        <select
          :value="selectedTeamId"
          class="team-selector"
          @change="onTeamChange"
        >
          <option
            v-for="team in authorizedEmailTeams"
            :key="team.id"
            :value="team.id"
          >
            {{ team.name }}
          </option>
        </select>
      </div>
    </div>
    <div v-if="showInboxSelector" class="input-group small">
      <label class="input-group-label">
        {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SEND_AS.LABEL') }}
      </label>
      <div class="flex-1 min-w-0 m-0 rounded-none whitespace-nowrap">
        <select
          :value="selectedInboxId"
          class="team-selector"
          @change="onInboxChange"
        >
          <option
            v-for="inbox in selectedTeamInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.email }}
          </option>
        </select>
      </div>
    </div>
    <div v-if="resolvedFromEmail" class="input-group small">
      <label class="input-group-label">
        {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.LABEL') }}
      </label>
      <div class="flex-1 min-w-0 m-0 rounded-none whitespace-nowrap">
        <span class="from-email">{{ resolvedFromEmail }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-group {
  @apply border-b border-solid border-n-weak my-1 flex items-center gap-2;
}

.input-group .input-group-label {
  @apply border-transparent bg-transparent text-xs font-semibold pl-0;
}

.team-selector {
  @apply w-full h-8 text-sm bg-transparent border-0 border-none outline-none cursor-pointer
    text-n-slate-12 dark:text-n-slate-12;
}

.from-email {
  @apply text-sm text-n-slate-11 py-1 inline-block;
}
</style>
